/**
 * ProjectList Component
 * Reusable project list with filters, sorting, and pagination
 * Used by: agencies, construction, architects, consulting, IT, photographers, events
 */

import { useState, useMemo } from 'react';
import { IndustryProject, ProjectStatus } from '@/types/industry';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from './StatusBadge';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  Edit,
  Eye,
  Calendar,
  Plus
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CompanyType } from '@/types/company';
import { AddExpenseToProjectModal } from './AddExpenseToProjectModal';

interface ProjectListProps {
  projects: IndustryProject[];
  isLoading?: boolean;
  onEdit?: (project: IndustryProject) => void;
  onView?: (project: IndustryProject) => void;
  clientMap?: Record<number, string>; // Optional client ID to name mapping
  showProfitability?: boolean; // Show profitability column
  pageSize?: number; // Items per page (default: 20)
  domain?: CompanyType; // Company type for domain-specific features
}

type SortField = 'name' | 'start_date' | 'budget_revenue' | 'profitability';
type SortOrder = 'asc' | 'desc';

export function ProjectList({
  projects,
  isLoading = false,
  onEdit,
  onView,
  clientMap = {},
  showProfitability = true,
  pageSize = 20,
  domain,
}: ProjectListProps) {
  // Modal state
  const [expenseModalOpen, setExpenseModalOpen] = useState(false);
  const [selectedProjectForExpense, setSelectedProjectForExpense] = useState<IndustryProject | null>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | 'all'>('all');
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [dateRangeFilter, setDateRangeFilter] = useState<'all' | 'active' | 'completed' | 'upcoming'>('all');

  // Sorting
  const [sortField, setSortField] = useState<SortField>('start_date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Get unique clients for filter
  const uniqueClients = useMemo(() => {
    const clientIds = new Set(projects.filter(p => p.client_id).map(p => p.client_id));
    return Array.from(clientIds).map(id => ({
      id,
      name: clientMap[id as number] || `Cliente ${id}`,
    }));
  }, [projects, clientMap]);

  // Filter projects
  const filteredProjects = useMemo(() => {
    let filtered = [...projects];

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(search) ||
          p.description?.toLowerCase().includes(search)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((p) => p.status === statusFilter);
    }

    // Client filter
    if (clientFilter !== 'all') {
      filtered = filtered.filter((p) => p.client_id?.toString() === clientFilter);
    }

    // Date range filter
    if (dateRangeFilter !== 'all') {
      const now = new Date();

      if (dateRangeFilter === 'active') {
        filtered = filtered.filter((p) => {
          if (!p.start_date) return false;
          const startDate = new Date(p.start_date);
          const endDate = p.end_date ? new Date(p.end_date) : null;
          return startDate <= now && (!endDate || endDate >= now);
        });
      } else if (dateRangeFilter === 'completed') {
        filtered = filtered.filter((p) => p.status === 'completed');
      } else if (dateRangeFilter === 'upcoming') {
        filtered = filtered.filter((p) => {
          if (!p.start_date) return false;
          const startDate = new Date(p.start_date);
          return startDate > now;
        });
      }
    }

    return filtered;
  }, [projects, searchTerm, statusFilter, clientFilter, dateRangeFilter]);

  // Sort projects
  const sortedProjects = useMemo(() => {
    const sorted = [...filteredProjects];

    sorted.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (sortField) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'start_date':
          aVal = a.start_date ? new Date(a.start_date).getTime() : 0;
          bVal = b.start_date ? new Date(b.start_date).getTime() : 0;
          break;
        case 'budget_revenue':
          aVal = a.budget_revenue || 0;
          bVal = b.budget_revenue || 0;
          break;
        case 'profitability':
          const aProfitability = a.actual_revenue > 0
            ? ((a.actual_revenue - a.actual_cost) / a.actual_revenue) * 100
            : 0;
          const bProfitability = b.actual_revenue > 0
            ? ((b.actual_revenue - b.actual_cost) / b.actual_revenue) * 100
            : 0;
          aVal = aProfitability;
          bVal = bProfitability;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredProjects, sortField, sortOrder]);

  // Paginate projects
  const paginatedProjects = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedProjects.slice(startIndex, endIndex);
  }, [sortedProjects, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedProjects.length / pageSize);

  // Handle sort toggle
  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  // Calculate profitability
  const calculateProfitability = (project: IndustryProject) => {
    if (project.actual_revenue === 0) return 0;
    return ((project.actual_revenue - project.actual_cost) / project.actual_revenue) * 100;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Carregando projetos...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar projetos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Status filter */}
        <Select value={statusFilter} onValueChange={(val) => setStatusFilter(val as ProjectStatus | 'all')}>
          <SelectTrigger>
            <SelectValue placeholder="Filtrar por status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="planning">Planejamento</SelectItem>
            <SelectItem value="active">Ativo</SelectItem>
            <SelectItem value="on_hold">Pausado</SelectItem>
            <SelectItem value="completed">Concluído</SelectItem>
            <SelectItem value="cancelled">Cancelado</SelectItem>
          </SelectContent>
        </Select>

        {/* Client filter */}
        {uniqueClients.length > 0 && (
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Filtrar por cliente" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os clientes</SelectItem>
              {uniqueClients.map((client) => (
                <SelectItem key={client.id} value={client.id!.toString()}>
                  {client.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Date range filter */}
        <Select value={dateRangeFilter} onValueChange={(val) => setDateRangeFilter(val as any)}>
          <SelectTrigger>
            <SelectValue placeholder="Filtrar por período" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os períodos</SelectItem>
            <SelectItem value="active">Em andamento</SelectItem>
            <SelectItem value="upcoming">Futuros</SelectItem>
            <SelectItem value="completed">Concluídos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Results count */}
      <div className="text-sm text-muted-foreground">
        {sortedProjects.length} {sortedProjects.length === 1 ? 'projeto encontrado' : 'projetos encontrados'}
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  className="-ml-3 h-8"
                  onClick={() => toggleSort('name')}
                >
                  Projeto
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  className="-ml-3 h-8"
                  onClick={() => toggleSort('start_date')}
                >
                  Datas
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>Status</TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  size="sm"
                  className="-ml-3 h-8"
                  onClick={() => toggleSort('budget_revenue')}
                >
                  Orçamento
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              {showProfitability && (
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="-ml-3 h-8"
                    onClick={() => toggleSort('profitability')}
                  >
                    Lucro
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                  </Button>
                </TableHead>
              )}
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedProjects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={showProfitability ? 7 : 6} className="text-center py-12 text-muted-foreground">
                  Nenhum projeto encontrado
                </TableCell>
              </TableRow>
            ) : (
              paginatedProjects.map((project) => {
                const profitability = calculateProfitability(project);

                return (
                  <TableRow key={project.id} className="cursor-pointer hover:bg-accent/50" onClick={() => onView?.(project)}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{project.name}</div>
                        {project.description && (
                          <div className="text-sm text-muted-foreground line-clamp-1">
                            {project.description}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {project.client_id ? (
                        <span className="text-sm">{clientMap[project.client_id] || `Cliente ${project.client_id}`}</span>
                      ) : (
                        <span className="text-sm text-muted-foreground">Sem cliente</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm space-y-1">
                        {project.start_date && (
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-muted-foreground" />
                            {format(new Date(project.start_date), 'dd/MM/yyyy', { locale: ptBR })}
                          </div>
                        )}
                        {project.end_date && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <span>até</span>
                            {format(new Date(project.end_date), 'dd/MM/yyyy', { locale: ptBR })}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={project.status} />
                    </TableCell>
                    <TableCell>
                      <div className="text-sm space-y-1">
                        {project.budget_revenue ? (
                          <>
                            <div className="font-medium">{formatCurrency(project.budget_revenue)}</div>
                            <div className="text-xs text-muted-foreground">
                              Real: {formatCurrency(project.actual_revenue)}
                            </div>
                          </>
                        ) : (
                          <span className="text-muted-foreground">Não definido</span>
                        )}
                      </div>
                    </TableCell>
                    {showProfitability && (
                      <TableCell>
                        {project.actual_revenue > 0 ? (
                          <Badge variant={profitability >= 20 ? 'default' : profitability >= 10 ? 'secondary' : 'destructive'}>
                            {profitability.toFixed(1)}%
                          </Badge>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {onView && (
                            <DropdownMenuItem onClick={() => onView(project)}>
                              <Eye className="mr-2 h-4 w-4" />
                              Visualizar
                            </DropdownMenuItem>
                          )}
                          {onEdit && (
                            <DropdownMenuItem onClick={() => onEdit(project)}>
                              <Edit className="mr-2 h-4 w-4" />
                              Editar
                            </DropdownMenuItem>
                          )}
                          {domain && (
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedProjectForExpense(project);
                                setExpenseModalOpen(true);
                              }}
                            >
                              <Plus className="mr-2 h-4 w-4" />
                              Adicionar Despesa
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Página {currentPage} de {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Próxima
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Add Expense Modal */}
      {domain && selectedProjectForExpense && (
        <AddExpenseToProjectModal
          open={expenseModalOpen}
          onOpenChange={setExpenseModalOpen}
          project={selectedProjectForExpense}
          domain={domain}
        />
      )}
    </div>
  );
}
